import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { Pass } from '@/types';
import { useDraftForm, clearDraft } from '@/shared/hooks/useDraftForm';
import { AppModal, DialogBox, FieldError, ModalContent, ModalTopBar } from '@/shared/ui';
import { TEST_IDS } from '@/testIds';
import {
  emptyPassFormValues,
  passFormToDraft,
  passToFormValues,
  type PassFormValues,
} from '../../model';
import { NameField } from './NameField';
import { DateRangeField } from './DateRangeField';
import { PassCreditsField, type PassCreditsMode } from './PassCreditsField';
import { PassModalFooter } from './PassModalFooter';
import styles from './PassModal.module.css';

interface PassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (pass: Omit<Pass, 'id'>) => void | Promise<void>;
  editPass?: Pass;
  isLoading?: boolean;
  placeholder?: ReactNode;
  onDelete?: (id: string) => void | Promise<void>;
  draftKey?: string;
  mode?: 'add' | 'edit';
  creditsMode?: PassCreditsMode;
}

const DRAFT_KEY = 'add-pass-v2';

export const PassModal: FC<PassModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editPass,
  isLoading = false,
  placeholder,
  onDelete,
  mode,
  creditsMode,
  draftKey = DRAFT_KEY,
}) => {
  const formMethods = useForm<PassFormValues>({
    defaultValues: isOpen ? passToFormValues(editPass) : emptyPassFormValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = formMethods;

  const resolvedMode = mode ?? (editPass ? 'edit' : 'add');
  const isEdit = resolvedMode === 'edit';
  const resolvedCreditsMode = creditsMode ?? (isEdit ? 'balance' : 'total');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const operationRef = useRef(false);
  const isBusy = isLoading || isSubmitting || isDeleting;

  const handleSave = async (values: PassFormValues): Promise<void> => {
    if (operationRef.current || isLoading) return;
    operationRef.current = true;
    clearErrors('root');
    try {
      await onSave(passFormToDraft(values));
      clearDraft(draftKey);
    } catch {
      setError('root', { message: 'Could not save the pass. Your changes are kept. Try again.' });
    } finally {
      operationRef.current = false;
    }
  };
  const submitForm = handleSubmit(handleSave);

  const formSource = useRef<Pass | null | undefined>(isOpen ? editPass : null);
  useEffect(() => {
    if (!isOpen) {
      formSource.current = null;
      return;
    }
    if (formSource.current === editPass) return;
    formSource.current = editPass;
    reset(passToFormValues(editPass));
  }, [editPass, isOpen, reset]);

  useEffect(() => {
    if (!isOpen) setIsDeleteDialogOpen(false);
  }, [isOpen]);

  // Initialize from the chosen template first, then restore the user's draft.
  useDraftForm<PassFormValues>({
    key: draftKey,
    watch,
    reset,
    enabled: resolvedMode === 'add' && isOpen,
  });

  const closeModal = useCallback((): void => {
    setIsDeleteDialogOpen(false);
    onClose();
    if (!isEdit) {
      clearDraft(draftKey);
    }
  }, [draftKey, isEdit, onClose]);

  const handleClose = (): void => {
    if (!isBusy && !operationRef.current) closeModal();
  };

  const handleDelete = (): void => {
    if (editPass && onDelete && !isBusy && !operationRef.current) {
      setIsDeleteDialogOpen(true);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!editPass || !onDelete || isBusy || operationRef.current) return;
    operationRef.current = true;
    setIsDeleting(true);
    clearErrors('root');
    try {
      await onDelete(editPass.id);
      closeModal();
    } catch {
      setError('root', { message: 'Could not delete the pass. Try again.' });
    } finally {
      operationRef.current = false;
      setIsDeleting(false);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <AppModal
        open={isOpen}
        dismissible={!isBusy}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
      >
        <form
          noValidate
          aria-label={isEdit ? 'Edit pass' : 'Add pass'}
          onSubmit={(event) => {
            event.preventDefault();
            if (!isBusy && !operationRef.current) void submitForm();
          }}
        >
          <ModalContent>
            <ModalTopBar
              title={isEdit ? 'Edit pass' : 'Add pass'}
              onCancel={handleClose}
              cancelDisabled={isBusy}
              cancelTestId={TEST_IDS.passModalCancelButton}
            />

            {placeholder ?? (
              <>
                <fieldset className={styles.body} disabled={isBusy}>
                  <NameField isEdit={isEdit} />
                  <DateRangeField />
                  <PassCreditsField mode={resolvedCreditsMode} />
                  <FieldError message={errors.root?.message} />
                </fieldset>

                <PassModalFooter
                  isEdit={isEdit}
                  isLoading={isLoading || isDeleting}
                  isSubmitting={isSubmitting}
                  canDelete={Boolean(editPass && onDelete)}
                  onDelete={handleDelete}
                />
              </>
            )}
          </ModalContent>
        </form>

        {editPass && onDelete && (
          <DialogBox
            isOpen={isDeleteDialogOpen}
            message={
              <>
                {`Delete pass "${editPass.name}"? This action cannot be undone.`}
                <FieldError message={errors.root?.message} />
              </>
            }
            confirmText="Delete"
            isDestructive
            isLoading={isBusy}
            confirmTestId={TEST_IDS.deletePassConfirmButton}
            onConfirm={() => void confirmDelete()}
            onCancel={() => {
              if (!isBusy) setIsDeleteDialogOpen(false);
            }}
          />
        )}
      </AppModal>
    </FormProvider>
  );
};
